import React, {
  forwardRef,
  useEffect,
  useRef,
  useImperativeHandle,
  memo,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { selectCurrentRound } from "../../../State/selectors";
import FreeSpinReelController from "./FreeSpinReelController";
import { safeInit } from "../../../utilities";

/**
 * FreeSpinReel
 * Simplified reel component for free spins — no spin animation.
 * Creates PIXI sprites programmatically through FreeSpinReelController.
 * Renders API symbols instantly when currentRound changes.
 */
const FreeSpinReel = memo(
  forwardRef(
    (
      {
        reel,
        index,
        x,
        y,
        symbolWidth,
        symbolHeight,
        symbolsSpriteSheet,
      },
      ref,
    ) => {
      const reelController = useRef(null);
      const symbolsContainerRef = useRef(null);
      const currentRound = useSelector(selectCurrentRound);

      // Initialize controller once
      useEffect(() => {
        if (symbolsContainerRef.current && !reelController.current) {
          safeInit(() => {
            reelController.current = new FreeSpinReelController(
              symbolsContainerRef.current,
              symbolsSpriteSheet,
              reel,
              index,
              symbolWidth,
              symbolHeight,
            );
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Update reel when currentRound changes (API data arrives)
      useEffect(() => {
        if (!reelController.current) return;
        const reelData = currentRound?.reels?.[index];
        if (reelData) {
          reelController.current.updateReel(reelData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound]);

      // Resize handler
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.resizeSymbol(symbolWidth, symbolHeight);
      }, [symbolWidth, symbolHeight]);

      // Cleanup
      useEffect(() => {
        return () => {
          reelController.current?.dispose();
          reelController.current = null;
        };
      }, []);

      // Expose controller to parent via ref
      useImperativeHandle(ref, () => ({
        getFreeSpinReelController: () => reelController.current,
      }));

      return (
        <Container
          ref={symbolsContainerRef}
          name={`free_spin_reel_${index}`}
          x={x}
          y={y}
        />
      );
    },
  ),
  (prev, next) =>
    prev.symbolWidth === next.symbolWidth &&
    prev.symbolHeight === next.symbolHeight &&
    prev.index === next.index &&
    prev.x === next.x &&
    prev.y === next.y,
);

export default FreeSpinReel;
